import * as React from 'react';
import { useEffect, useMemo, useRef } from 'react';
import { Eye } from 'lucide-react';

import { USER_PREFERENCE_CATEGORY } from '@klareospace/pharos-lib-types';
import type { Order, UserPreference } from '@klareospace/pharos-lib-types';
import type { DataTableColumn } from '@klareospace/pharos-lib-web-utils';
import { createActionsColumn, OrderStatusBadge, RowActionsMenu } from '@klareospace/pharos-lib-web-utils';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { userPreferenceKeys } from '../../user-preferences/api';
import { useColumnOrderPreference, useUpsertUserPreference, useUserPreferences } from '../../user-preferences/hooks';
import { CarrierDriverCell } from '../components/carrier-driver-cell';
import { DateSlotCell } from '../components/date-slot-cell';
import { EtaCell } from '../components/eta-cell';
import { LocationCell } from '../components/location-cell';
import { OrderNumberCell } from '../components/order-number-cell';
import { WeightVolumeCell } from '../components/weight-volume-cell';
import {
  DEFAULT_HIDDEN_COLUMNS,
  ORDERS_TABLE_COLUMN_ORDER_KEY,
  ORDERS_TABLE_HIDDEN_COLUMNS_KEY,
  ORDERS_TABLE_SHOWN_COLUMNS_KEY,
} from '../types/order.types';
import { formatHandlingUnitQuantity, translateStatusReason } from '../utils';

// ─── Constants ────────────────────────────────────────────────────────────────

/** Stable empty array returned when no hidden/shown-columns preference exists. */
const NO_COLUMNS: string[] = [];
const PREFERENCE_KEYS = [
  ORDERS_TABLE_HIDDEN_COLUMNS_KEY,
  ORDERS_TABLE_SHOWN_COLUMNS_KEY,
  ORDERS_TABLE_COLUMN_ORDER_KEY,
];

// ─── Types ────────────────────────────────────────────────────────────────────

export interface UseOrdersTableColumnsReturn {
  allColumns: DataTableColumn<Order>[];
  canResetColumns: boolean;
  columnOrder: string[];
  hiddenColumns: string[];
  isLoading: boolean;
  onColumnOrderChange: (order: string[]) => void;
  resetToDefault: () => void;
  saveColumns: () => void;
  toggleColumn: (columnKey: string) => void;
  visibleColumns: DataTableColumn<Order>[];
}

export interface UseOrdersTableColumnsInput {
  onOpenDetailDrawer: (order: Order) => void;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useOrdersTableColumns({ onOpenDetailDrawer }: UseOrdersTableColumnsInput): UseOrdersTableColumnsReturn {
  const { t } = useTranslation('orders');
  const queryClient = useQueryClient();

  const queryKey = userPreferenceKeys.list({ keys: PREFERENCE_KEYS });

  const { data: preferences, error, isLoading } = useUserPreferences({ keys: PREFERENCE_KEYS });
  const { mutate } = useUpsertUserPreference();

  useEffect(() => {
    if (error) {
      toast.error(t('table.columns.toast.loadError'));
    }
  }, [error]);

  const storedHiddenColumns = readColumnsPreference(preferences, ORDERS_TABLE_HIDDEN_COLUMNS_KEY);
  const storedShownColumns = readColumnsPreference(preferences, ORDERS_TABLE_SHOWN_COLUMNS_KEY);
  const hiddenColumns = useMemo(
    () => computeEffectiveHiddenColumns(storedHiddenColumns, storedShownColumns),
    [storedHiddenColumns, storedShownColumns],
  );

  const canResetColumns = storedHiddenColumns.length > 0 || storedShownColumns.length > 0;

  const { columnOrder, onColumnOrderChange } = useColumnOrderPreference({
    key: ORDERS_TABLE_COLUMN_ORDER_KEY,
    preferences,
  });

  const allColumns = useMemo<DataTableColumn<Order>[]>(
    () => [
      {
        alwaysOn: true,
        cell: (order): React.ReactNode => (
          <button
            className="text-left hover:underline focus:outline-none"
            onClick={(): void => onOpenDetailDrawer(order)}
            type="button"
          >
            <OrderNumberCell
              referenceNumber={order.referenceNumber}
              shipperLogo={order.shipper.logo}
              shipperName={order.shipper.name}
            />
          </button>
        ),
        className: 'min-w-[140px]',
        header: t('table.columns.orderNumber'),
        key: 'referenceNumber',
      },
      {
        alwaysOn: true,
        cell: (order): React.ReactNode => (
          <span className="text-sm">
            {order.customer.firstName} {order.customer.lastName}
          </span>
        ),
        className: 'min-w-[160px]',
        header: t('table.columns.customer'),
        key: 'customer.firstName',
        sortable: true,
      },
      {
        alwaysOn: true,
        cell: (order): React.ReactNode => (
          <OrderStatusBadge
            label={t(`status.${order.status}`)}
            reasonLabel={translateStatusReason(t, order.statusReason)}
            status={order.status}
          />
        ),
        className: 'min-w-[160px]',
        header: t('table.columns.status'),
        key: 'status',
        sortable: true,
      },
      {
        cell: (order): React.ReactNode => (
          <LocationCell siteName={order.pickup.site.name} siteType={order.pickup.site.type} />
        ),
        className: 'min-w-[160px]',
        header: t('table.columns.pickup'),
        key: 'pickupSite',
      },
      {
        cell: (order): React.ReactNode => (
          <LocationCell
            address={order.delivery.address}
            requestedAddress={order.delivery.requestedAddress}
            siteName={order.delivery.site?.name}
            siteType={order.delivery.site?.type}
          />
        ),
        className: 'min-w-[200px]',
        header: t('table.columns.delivery'),
        key: 'delivery',
      },
      {
        alwaysOn: true,
        cell: (order): React.ReactNode => (
          <DateSlotCell initialTimeWindow={order.initialTimeWindow} requestedTimeWindow={order.requestedTimeWindow} />
        ),
        className: 'min-w-[240px]',
        header: t('table.columns.dateSlot'),
        key: 'requestedTimeWindow.start',
        sortable: true,
      },
      {
        cell: (order): React.ReactNode => <EtaCell eta={order.eta} />,
        className: 'min-w-[120px]',
        header: t('table.columns.eta'),
        key: 'eta.value',
        sortable: true,
      },
      {
        cell: (order): React.ReactNode => <CarrierDriverCell assignedTo={order.assignedTo} />,
        className: 'min-w-[180px]',
        header: t('table.columns.carrierDriver'),
        key: 'assignedTo.carrier.name',
        sortable: true,
      },
      {
        cell: (order): React.ReactNode => (
          <span className="text-sm">{formatHandlingUnitQuantity(order.totalHandlingUnitQuantity)}</span>
        ),
        className: 'min-w-[80px]',
        header: t('table.columns.qty'),
        key: 'totalHandlingUnitQuantity',
      },
      {
        cell: (order): React.ReactNode => (
          <WeightVolumeCell totalVolume={order.totalVolume} totalWeight={order.totalWeight} />
        ),
        className: 'min-w-[120px]',
        header: t('table.columns.weightVolume'),
        key: 'totalWeight',
      },
      createActionsColumn<Order>(
        (order) => (
          <RowActionsMenu
            actions={[
              {
                icon: <Eye className="w-4 h-4" />,
                label: t('table.viewDetails'),
                onClick: (): void => onOpenDetailDrawer(order),
              },
            ]}
          />
        ),
        { alwaysOn: true },
      ),
    ],
    [t, onOpenDetailDrawer],
  );

  const visibleColumns = useMemo(
    () => allColumns.filter((col) => !hiddenColumns.includes(col.key)),
    [allColumns, hiddenColumns],
  );

  const dirtyKeysRef = useRef<Set<string>>(new Set());

  function updateHiddenColumns(value: string[]): void {
    updatePreferenceValue(queryClient, queryKey, ORDERS_TABLE_HIDDEN_COLUMNS_KEY, value);
    dirtyKeysRef.current.add(ORDERS_TABLE_HIDDEN_COLUMNS_KEY);
  }

  function updateShownColumns(value: string[]): void {
    updatePreferenceValue(queryClient, queryKey, ORDERS_TABLE_SHOWN_COLUMNS_KEY, value);
    dirtyKeysRef.current.add(ORDERS_TABLE_SHOWN_COLUMNS_KEY);
  }

  function saveColumns(): void {
    if (dirtyKeysRef.current.has(ORDERS_TABLE_HIDDEN_COLUMNS_KEY)) {
      mutate({
        category: USER_PREFERENCE_CATEGORY.FRONTOFFICE,
        key: ORDERS_TABLE_HIDDEN_COLUMNS_KEY,
        value: storedHiddenColumns,
      });
    }
    if (dirtyKeysRef.current.has(ORDERS_TABLE_SHOWN_COLUMNS_KEY)) {
      mutate({
        category: USER_PREFERENCE_CATEGORY.FRONTOFFICE,
        key: ORDERS_TABLE_SHOWN_COLUMNS_KEY,
        value: storedShownColumns,
      });
    }
    dirtyKeysRef.current.clear();
  }

  function toggleColumn(columnKey: string): void {
    if (DEFAULT_HIDDEN_COLUMNS.includes(columnKey)) {
      const newShownColumns = storedShownColumns.includes(columnKey)
        ? storedShownColumns.filter((key) => key !== columnKey)
        : [...storedShownColumns, columnKey];
      updateShownColumns(newShownColumns);
      return;
    }

    const newHiddenColumns = storedHiddenColumns.includes(columnKey)
      ? storedHiddenColumns.filter((key) => key !== columnKey)
      : [...storedHiddenColumns, columnKey];
    updateHiddenColumns(newHiddenColumns);
  }

  function resetToDefault(): void {
    updateHiddenColumns([]);
    updateShownColumns([]);
  }

  return {
    allColumns,
    canResetColumns,
    columnOrder,
    hiddenColumns,
    isLoading,
    onColumnOrderChange,
    resetToDefault,
    saveColumns,
    toggleColumn,
    visibleColumns,
  };
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function isStringColumnArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every((item) => typeof item === 'string');
}

function readColumnsPreference(preferences: UserPreference[] | undefined, key: string): string[] {
  const pref = preferences?.find((p) => p.key === key);
  return isStringColumnArray(pref?.value) ? pref.value : NO_COLUMNS;
}

/**
 * A column in `DEFAULT_HIDDEN_COLUMNS` is hidden unless explicitly opted into via the shown-list
 * preference; every other column follows the regular opt-out hidden-list preference.
 */
function computeEffectiveHiddenColumns(storedHiddenColumns: string[], storedShownColumns: string[]): string[] {
  const hiddenOptOutColumns = storedHiddenColumns.filter((key) => !DEFAULT_HIDDEN_COLUMNS.includes(key));
  const hiddenOptInColumns = DEFAULT_HIDDEN_COLUMNS.filter((key) => !storedShownColumns.includes(key));
  return [...hiddenOptOutColumns, ...hiddenOptInColumns];
}

function updatePreferenceValue(
  queryClient: ReturnType<typeof useQueryClient>,
  queryKey: readonly unknown[],
  key: string,
  value: string[],
): void {
  queryClient.setQueryData<UserPreference[]>(queryKey, (prev = []) => {
    const exists = prev.some((p) => p.key === key);
    if (exists) {
      return prev.map((p) => (p.key === key ? { ...p, value } : p));
    }
    return [...prev, { category: USER_PREFERENCE_CATEGORY.FRONTOFFICE, key, value } as UserPreference];
  });
}
